/**
 * Carregar dado de uma tela: `carregando · erro · dado`, e um `recarregar()`.
 *
 * Trinta linhas em vez de uma biblioteca de cache, e por uma razão de projeto: estas
 * são telas de ADMINISTRAÇÃO. Depois de gravar, o que se quer ver é o que o servidor
 * gravou — não o que um cache otimista supôs. `recarregar()` é a operação certa, e
 * um cache aqui só criaria a chance de a tela mostrar o estado anterior.
 *
 * A corrida está tratada: um pedido que volta depois de outro mais novo é
 * descartado, e o `AbortController` cancela o anterior. Sem isso, alternar de
 * catálogo depressa deixaria na tela a resposta do catálogo que já não está aberto.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ErroDaApi } from './cliente.ts'
import { useEntidadeAtiva } from '../shell/contexto-de-entidade.tsx'

export interface Recurso<T> {
  readonly dado: T | null
  readonly carregando: boolean
  readonly erro: ErroDaApi | null
  readonly recarregar: () => void
  readonly definir: (valor: T) => void
}

interface EstadoDoRecurso<T> {
  readonly identidade: symbol
  readonly dado: T | null
  readonly carregando: boolean
  readonly erro: ErroDaApi | null
}

export function useRecurso<T>(buscar: (sinal: AbortSignal) => Promise<T>, dependencias: unknown[]): Recurso<T> {
  // `buscar` é recriada a cada render (é uma seta no corpo do componente). Guardá-la
  // numa referência é o que permite declarar as dependências REAIS do pedido — quem
  // chama sabe o que o muda; a identidade da função, não.
  const daVez = useRef(buscar)
  daVez.current = buscar

  /*
   * O header faz parte da identidade de TODA leitura, mesmo quando a tela esquece de
   * repeti-lo na sua lista de dependências. É a causa comum da troca A → B: o cliente
   * HTTP já passa B imediatamente, portanto o recurso também precisa nascer de novo.
   *
   * Recursos mestres continuam globais dentro do ambiente porque quem determina o
   * recorte é a API; relê-los não os transforma em fatos exclusivos da entidade.
   */
  const entidadeAtiva = useEntidadeAtiva()
  const dependenciasEfetivas = [...dependencias, entidadeAtiva]
  const identidade = useMemo(() => Symbol('recurso'), dependenciasEfetivas)
  const identidadeAtual = useRef(identidade)
  identidadeAtual.current = identidade

  const [estado, setEstado] = useState<EstadoDoRecurso<T>>({
    identidade,
    dado: null,
    carregando: true,
    erro: null,
  })
  const [tentativa, setTentativa] = useState(0)

  const recarregar = useCallback(() => {
    setTentativa((n) => n + 1)
  }, [])

  useEffect(() => {
    const controle = new AbortController()
    let vivo = true
    const identidadeDoPedido = identidade
    setEstado((anterior) => ({
      identidade: identidadeDoPedido,
      dado: anterior.identidade === identidadeDoPedido ? anterior.dado : null,
      carregando: true,
      erro: null,
    }))

    daVez.current(controle.signal)
      .then((valor) => {
        if (vivo) setEstado({ identidade: identidadeDoPedido, dado: valor, carregando: false, erro: null })
      })
      .catch((causa: unknown) => {
        if (!vivo) return
        setEstado({
          identidade: identidadeDoPedido,
          dado: null,
          carregando: false,
          erro: causa instanceof ErroDaApi ? causa : new ErroDaApi('INTERNAL', 'Não foi possível carregar.'),
        })
      })

    return () => {
      vivo = false
      controle.abort()
    }
  }, [identidade, tentativa])

  const definir = useCallback((valor: T) => {
    setEstado({ identidade: identidadeAtual.current, dado: valor, carregando: false, erro: null })
  }, [])

  // A dependência pode mudar entre o render e o efeito. Nesse intervalo o dado da
  // geração anterior já fica invisível, impedindo cabeçalho B sobre conteúdo de A.
  if (estado.identidade !== identidade) {
    return { dado: null, carregando: true, erro: null, recarregar, definir }
  }
  return { dado: estado.dado, carregando: estado.carregando, erro: estado.erro, recarregar, definir }
}
